import { Widget } from "../core/base/widget";
import { utilsService } from "../core/services/utils.service";
import { IKendoJQuery } from "../core/models/kendo-jquery";
import {
    DataManager,
    ChatView,
    MessageBox,
    MessageMenu,
    AccesibilityManager,
    EndlessRangeManager,
    EndlessScrollCoordinator,
    RemoteRangeLoader
} from "./collaborators";
import type {
    IRemoteParsedResponse,
    IRemoteRangeRequestOptions,
    RemoteLoadingState
} from "./collaborators";
import {
    STYLES,
    REFERENCES,
    EVENTS,
    ICONS,
    COMMANDS,
    CHANGE,
    DOT,
    LTR,
    DEFAULT_PAGE_SIZE
} from "./constants";
import {
    renderMessageGroup,
    renderMessageReference,
    renderFiles,
    renderSuggestions,
    renderHeader
} from "./templates";
import type {
    IChatOptions,
    IMessage,
    IMessageReferenceData,
    IMenuAction
} from "./models";
import type { IFile } from "../upload";
import { CommandHandler, EventWiring } from "./helpers";

declare const $: any;

const DEFAULT_MESSAGE_ACTIONS: IMenuAction[] = [
    { name: COMMANDS.reply, text: "Reply", icon: ICONS.undo },
    { name: COMMANDS.copy, text: "Copy", icon: ICONS.copy },
    { name: COMMANDS.pin, text: "Pin", icon: ICONS.pin },
    { name: COMMANDS.delete, text: "Delete", icon: ICONS.trash }
];

const DEFAULT_FILE_ACTIONS: IMenuAction[] = [
    { name: COMMANDS.download, text: "Download", icon: ICONS.download }
];

const DEFAULT_MESSAGES = {
    messageListLabel: "Message list",
    placeholder: "Type a message...",
    sendButton: "Send message",
    sendButtonLoading: "Stop",
    actionButton: "Send message",
    actionButtonLoading: "Stop",
    stopButton: "Stop",
    speechToTextButton: "Toggle speech to text",
    fileButton: "Attach file",
    downloadAll: "Download all",
    selfMessageDeleted: "You removed this message.",
    otherMessageDeleted: "This message was removed by its sender.",
    stopGeneration: "Stop generation",
    messageBoxLabel: "Type your message here",
    pinnedMessageCloseButton: "Unpin message",
    replyMessageCloseButton: "Remove reply",
    fileMenuButton: "File menu",
    retryMessage: "Retry"
};

interface IRefreshState {
    data: IMessage[];
    changedItems: IMessage[];
    pinnedMessage: IMessageReferenceData | null;
    totalCount: number;
    addedCount: number;
}

export class Chat extends Widget<IChatOptions> {
    static options: Partial<IChatOptions> = {
        name: "Chat",
        scrollMode: "scrollable",
        pageSize: DEFAULT_PAGE_SIZE,
        endlessScrollDebounceDelay: 150,
        autoAssignId: true,
        autoBind: true,
        allowMessageCollapse: false,
        showUsername: true,
        showAvatar: true,
        timestampVisibility: "onFocus",
        suggestionsBehavior: "send",
        authorId: null,
        suggestionsScrollable: false,
        suggestedActionsScrollable: false,
        suggestionsLayoutMode: "scroll",
        suggestedActionsLayoutMode: "scroll",
        speechToText: true,
        fileAttachment: true,
        scrollToBottomButton: true,
        autoScrollThreshold: "20%",
        loading: false,
        messageBox: {
            mode: "multi",
            maxTextAreaHeight: 110
        },
        messageToolbarActions: [],
        messageActions: DEFAULT_MESSAGE_ACTIONS,
        fileActions: DEFAULT_FILE_ACTIONS,
        suggestions: [],
        headerItems: [],
        dir: LTR,
        messageTimeFormat: "ddd MMM dd yyyy",
        width: null,
        height: null,
        messages: DEFAULT_MESSAGES,
        messageWidthMode: "standard",
        messageTemplate: null,
        messageGroupTemplate: renderMessageGroup,
        messageReferenceTemplate: renderMessageReference,
        filesTemplate: renderFiles,
        suggestionsTemplate: null,
        suggestedActionsTemplate: renderSuggestions,
        suggestedActionsField: "suggestedActions",
        timestampTemplate: null,
        messageStatusTemplate: null,
        messageStatusSettings: null,
        headerTemplate: null,
        noDataTemplate: null,
        messageContentTemplate: null,
        userStatusTemplate: null,
        attachmentTemplate: null,
        messageBoxTemplate: null,
        authorMessageSettings: null,
        receiverMessageSettings: null,
        actionButton: null,
        attachmentLayout: "list",
        filesLayoutMode: "vertical",
        pinnedMessages: [],
        referenceResolver: null,
        textField: "text",
        authorIdField: "authorId",
        authorNameField: "authorName",
        authorImageUrlField: "authorImageUrl",
        authorImageAltTextField: "authorImageAltText",
        idField: "id",
        timestampField: "timestamp",
        filesField: "files",
        replyToIdField: "replyToId",
        isDeletedField: "isDeleted",
        isPinnedField: "isPinned",
        isTypingField: "isTyping",
        statusField: "status",
        failedField: "failed",
        attachmentsField: "attachments",
        attachmentLayoutField: "attachmentLayout",
        skipSanitization: false
    };

    _dataManager!: DataManager;
    view!: ChatView;
    messageBox!: MessageBox;
    private messageContextMenu!: MessageMenu;
    private accessibility!: AccesibilityManager;
    private _commandHandler!: CommandHandler;
    private _eventWiring!: EventWiring;
    private currentMessageReplyId: string | null = null;
    private _hasHeader = false;
    private _refreshHandler!: () => void;
    private _loading = false;
    private _renderStartIndex = 0;
    private _renderEndIndex = 0;
    private _endlessPageSize = DEFAULT_PAGE_SIZE;
    private _endlessRangeManager = new EndlessRangeManager();
    private _endlessScrollCoordinator!: EndlessScrollCoordinator;
    private _remoteRangeLoader!: RemoteRangeLoader;
    dataSource: any;

    events: string[] = [
        EVENTS.sendMessage,
        EVENTS.suggestionClick,
        EVENTS.unpin,
        EVENTS.input,
        EVENTS.toolbarAction,
        EVENTS.fileMenuAction,
        EVENTS.contextMenuAction,
        EVENTS.download,
        EVENTS.fileSelect,
        EVENTS.fileRemove,
        EVENTS.executeAction,
        EVENTS.resendMessage,
        EVENTS.referencedMessageClick
    ];

    /**
     * Creates a new Chat widget instance.
     *
     * @param element - The DOM element to attach the widget to
     * @param options - Configuration options
     * @param events - Optional event handlers
     */
    constructor(
        element: HTMLElement | IKendoJQuery,
        options?: Partial<IChatOptions>,
        events?: string[]
    ) {
        options = options || {};
        Chat.mergeContextMenuActions(options, Chat.options, "messageActions");
        Chat.mergeContextMenuActions(options, Chat.options, "fileActions");

        super(element, $.extend(true, {}, Chat.options, options));

        (this.options as IChatOptions).messageActions = options.messageActions as IMenuAction[];
        (this.options as IChatOptions).fileActions = options.fileActions as IMenuAction[];

        if (events) {
            this._events = events as any;
        }

        this._init(options);
    }

    static mergeContextMenuActions(
        options: Partial<IChatOptions>,
        defaultOptions: Partial<IChatOptions>,
        property: "messageActions" | "fileActions"
    ): void {
        const userActions = options[property];

        if (Array.isArray(userActions)) {
            const defaultActions = defaultOptions[property] || [];
            const defaultMap: Record<string, IMenuAction> = {};

            for (const action of defaultActions) {
                defaultMap[action.name] = { ...action };
            }

            options[property] = userActions.map(userAction => {
                if (userAction.name in defaultMap) {
                    return { ...defaultMap[userAction.name], ...userAction };
                }
                return { ...userAction };
            });
        } else {
            options[property] = defaultOptions[property] || [];
        }
    }

    private _init(options: Partial<IChatOptions>): void {
        this.currentMessageReplyId = null;
        this._hasHeader = !!(options.headerItems?.length);

        this._initUser();
        this._initWrapper();
        this._initDataSource();

        this._validateRemoteEndlessStartup();

        this._initView();

        if (options.toolbar) {
            utilsService.logToConsole("The 'toolbar' option has been deprecated.", "warn");
        }

        this._initMessageBox();
        this._initMenus();
        this._initAccessibility();
        this._initRemoteRangeLoader();
        this._initEndlessScrollCoordinator();

        this._attachEvents();

        if (this.options.loading) {
            this.loading(true);
        }

        if (this._isEndlessScrollEnabled()) {
            this.view._scrollManager._isLoadingOlder = true;
        }

        if (this.options.autoBind) {
            if (this._isRemoteEndlessScroll()) {
                this._requestLatestRemoteStartupRange();
            } else {
                this.dataSource.fetch();
            }
        }

        this.scrollToBottom();
    }

    private _validateRemoteEndlessStartup(): void {
        if (this._isRemoteEndlessScroll() && this.options.autoBind === false) {
            throw new Error("The Chat remote endless mode does not support autoBind: false.");
        }
    }

    private _requestLatestRemoteStartupRange(): void {
        this._endlessScrollCoordinator.requestLatestRemoteStartupRange();
    }

    private _initUser(): void {
        const options = this.options as IChatOptions;

        if (options.authorId) {
            options.authorId = options.authorId.toString();
            return;
        }

        options.authorId = utilsService.guid();
    }

    private _initWrapper(): void {
        const options = this.options as IChatOptions;
        const height = options.height;
        const width = options.width;
        const headerItems = options.headerItems;
        const headerTemplate = options.headerTemplate;
        const uiElements = `<div ${REFERENCES.viewWrapper}></div>`;

        this.wrapper = this.element
            .addClass(STYLES.wrapper)
            .attr("dir", options.dir)
            .append(uiElements);

        if (this._hasHeader || headerTemplate) {
            this.wrapper.prepend(renderHeader(headerItems, headerTemplate));
        }

        if (height) {
            this.wrapper.css({
                height: height,
                minHeight: height
            });
        }

        if (width) {
            this.wrapper.css({
                width: width,
                minWidth: width
            });
        }
    }

    private _initDataSource(): void {
        this._refreshHandler = this.refresh.bind(this);

        this._dataManager = new DataManager({
            chatOptions: this.options as IChatOptions
        });
        this.dataSource = this._dataManager.getDataSource();

        this._syncEndlessPageSize();

        this.dataSource.bind(CHANGE, this._refreshHandler);
    }

    private _syncEndlessPageSize(): void {
        const options = this.options as IChatOptions;

        this._endlessPageSize = this._endlessRangeManager.syncPageSize(this._isEndlessScrollEnabled(options), this.dataSource);
    }

    private _getEndlessPageSize(): number {
        return this._endlessPageSize;
    }

    private _unbindDataSource(): void {
        this.dataSource.unbind(CHANGE, this._refreshHandler);
        (this as any)._dataManager = null;
    }

    private _initView(): void {
        const options = $.extend(true, {}, this.options);
        delete options.name;

        const element = this.wrapper!.find(`[${REFERENCES.viewWrapper}]`) as IKendoJQuery;

        const context: any = {
            dataManager: this._dataManager
        };

        if (this._isEndlessScrollEnabled()) {
            context.onScrollNearTop = this._loadOlderMessages.bind(this);
            context.onScrollNearBottom = this._loadNewerMessages.bind(this);
            context.onScrollToLatest = this._prepareLatestMessages.bind(this);
            context.isLatestRangeActive = this._isLatestRenderedRange.bind(this);
            context.hasLatestMessageRendered = this._hasRenderedLatestMessage.bind(this);
        }

        this.view = new ChatView(element, options, context);
    }

    private _initMessageBox(): void {
        const options = $.extend(true, {}, this.options);
        delete options.name;

        this.messageBox = new MessageBox($('<textarea></textarea>'), options, {
            chatElement: this.wrapper!
        });
    }

    private _initMenus(): void {
        const options = this.options as IChatOptions;
        const messageActions = options.messageActions;

        this.messageContextMenu = new MessageMenu($("<ul></ul>"), { dataSource: messageActions, target: this.wrapper });
    }

    private _initAccessibility(): void {
        this.accessibility = new AccesibilityManager({
            wrapper: this.wrapper!,
            getOptions: () => this.options
        });

        this.accessibility.setupAriaAttributes();
        this.accessibility.setupBubbleTabNavigation();
    }

    private _initRemoteRangeLoader(): void {
        this._remoteRangeLoader = new RemoteRangeLoader({
            getDataSource: () => this.dataSource,
            getChatOptions: () => this.options as IChatOptions,
            getTotalCount: () => this._getEndlessDataCount(),
            getLoadedRange: () => this._dataManager.getLoadedRange(),
            getRenderedRange: () => ({
                startIndex: this._renderStartIndex,
                endIndex: this._renderEndIndex
            }),
            getLoadedMessagesInRange: this._dataManager.getMessagesInRange.bind(this._dataManager),
            getLoadedMessageById: this._dataManager.getLoadedMessageById.bind(this._dataManager),
            getReferenceMessageById: this._dataManager.getAuxiliaryMessageById.bind(this._dataManager),
            cacheResolvedReferenceMessages: this._dataManager.cacheResolvedReferenceMessages.bind(this._dataManager),
            applyResponse: this._applyRemoteRangeResponse.bind(this),
            updateEndlessState: this._updateEndlessState.bind(this),
            completeEndlessLoad: this._completeEndlessLoad.bind(this),
            setRemoteLoadingState: this._setRemoteLoadingState.bind(this),
            canRequestRange: () => !!(this.view && this.dataSource)
        });
    }

    private _initEndlessScrollCoordinator(): void {
        this._endlessScrollCoordinator = new EndlessScrollCoordinator({
            beginLatestRemoteStartup: this._beginLatestRemoteStartupRangeRequest.bind(this),
            isEndlessScrollEnabled: this._isEndlessScrollEnabled.bind(this),
            isRemoteEndlessScroll: this._isRemoteEndlessScroll.bind(this),
            getPageSize: this._getEndlessPageSize.bind(this),
            getTotalCount: this._getEndlessDataCount.bind(this),
            getRenderedRange: () => ({
                startIndex: this._renderStartIndex,
                endIndex: this._renderEndIndex
            }),
            getLoadedRange: () => this._dataManager.getLoadedRange(),
            getLatestRange: this._getLatestRange.bind(this),
            getTargetRange: this._getTargetRange.bind(this),
            isLatestRenderedRange: this._isLatestRenderedRange.bind(this),
            isRangeLoaded: (startIndex: number, endIndex: number) => this._dataManager.isRangeLoaded(startIndex, endIndex),
            getMessageIndexById: (id: string | number) => this._dataManager.getMessageIndexById(id),
            requestRemoteRange: (options: IRemoteRangeRequestOptions) => this._remoteRangeLoader.requestRange(options),
            renderRange: (startIndex: number, endIndex: number, options = {}) => this._renderEndlessRange(startIndex, endIndex, options),
            scrollToBottom: this.scrollToBottom.bind(this),
            refreshScrollState: () => this.view.refreshScrollState(),
            scrollToMessage: this.scrollToMessage.bind(this),
            prependLocalRange: this._prependLocalEndlessRange.bind(this),
            appendLocalRange: this._appendLocalEndlessRange.bind(this),
            updateEndlessState: this._updateEndlessState.bind(this)
        });
    }

    private _beginLatestRemoteStartupRangeRequest(): void {
        this.view._scrollManager._isLoadingOlder = true;
        this.view._scrollManager._isLoadingNewer = false;
        this.view.clearMessages();
    }

    private _setRemoteLoadingState(direction: RemoteLoadingState): void {
        if (!this.view) {
            return;
        }

        if (direction === "older" || direction === "all") {
            this.view._scrollManager._isLoadingOlder = true;
        }

        if (direction === "newer" || direction === "all") {
            this.view._scrollManager._isLoadingNewer = true;
        }

        this.view.refreshScrollState();
    }

    private _applyRemoteRangeResponse(
        requestOptions: IRemoteRangeRequestOptions,
        parsedResponse: IRemoteParsedResponse
    ): void {
        const nextModels = this._updateRemoteRangeWindow(
            requestOptions.mode,
            parsedResponse.range.startIndex,
            parsedResponse.models,
            parsedResponse.totalCount
        );

        if (requestOptions.mode === "prepend") {
            this._applyPrependedRemoteRange(parsedResponse.range, nextModels);
        } else if (requestOptions.mode === "append") {
            this._applyAppendedRemoteRange(parsedResponse.range, nextModels);
        } else {
            this._applyReplacedRemoteRange(requestOptions, parsedResponse.range);
        }

        this._refreshPinnedState(this._dataManager.getPinnedMessage());
    }

    private _updateRemoteRangeWindow(
        mode: IRemoteRangeRequestOptions["mode"],
        startIndex: number,
        models: IMessage[],
        totalCount: number
    ): IMessage[] {
        const nextModels = models;
        const wasAutoSync = this.dataSource.options.autoSync;

        this.dataSource.options.autoSync = false;
        this.dataSource.unbind(CHANGE, this._refreshHandler);

        const data = this.dataSource.data();

        (data as any).omitChangeEvent = true;
        try {
            if (mode === "replace") {
                data.splice(0, data.length, ...models);
                this._dataManager.setRemoteRangeStart(startIndex);
            } else if (mode === "prepend") {
                data.splice(0, 0, ...nextModels);
                this._dataManager.setRemoteRangeStart(startIndex);
            } else {
                data.splice(data.length, 0, ...nextModels);
            }
        } finally {
            (data as any).omitChangeEvent = false;
        }

        this.dataSource.bind(CHANGE, this._refreshHandler);
        this.dataSource.options.autoSync = wasAutoSync;
        this.dataSource._total = totalCount;
        this.dataSource._pristineTotal = totalCount;

        return nextModels;
    }

    private _applyPrependedRemoteRange(
        range: { startIndex: number; endIndex: number },
        nextModels: IMessage[]
    ): void {
        if (this.view && nextModels.length) {
            this.view._scrollManager.preserveScrollPosition(() => {
                this.view._messageRenderer.prependMessages(nextModels);
            });
        }

        this._renderStartIndex = Math.min(range.startIndex, this._renderStartIndex);
        this._renderEndIndex = Math.max(this._renderEndIndex, range.endIndex);
        this._updateEndlessState();
        this._finalizeIncrementalEndlessRender();
    }

    private _applyAppendedRemoteRange(
        range: { startIndex: number; endIndex: number },
        nextModels: IMessage[]
    ): void {
        nextModels.forEach((message: IMessage) => {
            this.renderMessage(message);
        });

        this._renderEndIndex = Math.max(this._renderEndIndex, range.endIndex);
        this._updateEndlessState();
        this._finalizeIncrementalEndlessRender();
    }

    private _applyReplacedRemoteRange(
        requestOptions: IRemoteRangeRequestOptions,
        range: { startIndex: number; endIndex: number }
    ): void {
        const targetMessage = requestOptions.targetMessageId
            ? this._dataManager.getLoadedMessageById(requestOptions.targetMessageId)
            : null;

        this._renderEndlessRange(range.startIndex, range.endIndex, {
            scrollToBottom: requestOptions.scrollToBottom,
            scrollToMessageUid: targetMessage?.uid
        });
    }

    private _finalizeIncrementalEndlessRender(): void {
        if (!this.view) {
            return;
        }

        this.view.renderSuggestedActions();
        this.view.updateSeparator();
        this.view.refreshScrollState();

        if (!this.accessibility) {
            return;
        }

        this.accessibility.setupAriaAttributes();
        this.accessibility.setupBubbleTabNavigation();
    }

    private _attachEvents(): void {
        const options = this.options as IChatOptions;

        this._commandHandler = new CommandHandler({
            getUserId: this.getUserId.bind(this),
            dataItem: this.dataItem.bind(this),
            fileDataItem: this.fileDataItem.bind(this),
            removeMessage: this.removeMessage.bind(this),
            pinMessage: (message: IMessage) => this._dataManager.pinMessage(message),
            setReplyMessage: (message: IMessage, isOwnMessage: boolean) => this.messageBox.setReplyMessage(message, isOwnMessage),
            setCurrentMessageReplyId: (id: string) => { this.currentMessageReplyId = id; },
            setupAriaAttributes: () => this.accessibility.setupAriaAttributes(),
            setContextMenuActions: (isOwnMessage: boolean) => {
                const settings = isOwnMessage ? options.authorMessageSettings : options.receiverMessageSettings;
                this.messageContextMenu.setActions(settings?.messageActions ?? options.messageActions);
            },
            toggleDeleteVisibility: (isAuthor: boolean) => this.messageContextMenu.toggleDeleteVisibility(isAuthor),
            isContextMenuAllowed: (isOwnMessage: boolean) => {
                const settings = isOwnMessage ? options.authorMessageSettings : options.receiverMessageSettings;
                return settings?.enableContextMenuActions !== false;
            },
            trigger: this.trigger.bind(this)
        });

        this._eventWiring = new EventWiring({
            options: this.options as IChatOptions,
            wrapper: this.wrapper!,
            view: this.view,
            messageBox: this.messageBox,
            messageContextMenu: this.messageContextMenu,
            value: this.value.bind(this),
            postMessage: this.postMessage.bind(this),
            clearReplyState: this.clearReplyState.bind(this),
            prepareLatestMessages: this._prepareLatestMessages.bind(this),
            commandExecute: this._commandHandler.commandExecute.bind(this._commandHandler),
            contextMenuExecute: this._commandHandler.contextMenuExecute.bind(this._commandHandler),
            messageContextMenuOpen: this._commandHandler.messageContextMenuOpen.bind(this._commandHandler),
            dataItem: this.dataItem.bind(this),
            scrollToMessage: this.scrollToMessage.bind(this),
            navigateToReferencedMessage: this._navigateToReferencedMessage.bind(this),
            clearPinnedMessage: this.clearPinnedMessage.bind(this),
            getPinnedMessage: () => this._dataManager.getPinnedMessage(),
            getMessageById: (id: string) => this._dataManager.getMessageById(id),
            trigger: this.trigger.bind(this),
            accessibility: this.accessibility,
            messageBoxRef: this.messageBox
        });

        this._eventWiring.attach();

        this.bind(this.events, this.options);
    }

    getUserId(): string {
        return (this.options as IChatOptions).authorId!;
    }

    /**
     * Gets or sets the input value of the message box.
     * @param value - Optional value to set. If provided, sets the input value.
     * @returns The current input value when called without arguments.
     */
    value(newValue?: string): string | void {
        if (newValue === undefined) {
            return this.messageBox?.value() || "";
        }

        if (this.messageBox) {
            this.messageBox.value(newValue);
            this.trigger(EVENTS.input, { value: newValue });
        }
    }

    /**
     * Sets new options for the widget.
     */
    setOptions(options: Partial<IChatOptions>): void {
        super.setOptions(options);
        this.destroy();
        const chat = new Chat(this.element, this.options as Partial<IChatOptions>);
        Object.assign(this, chat);
    }

    /**
     * Sets a new data source.
     */
    setDataSource(dataSource: any): void {
        if (this.dataSource) {
            this._unbindDataSource();
        }

        this._remoteRangeLoader.reset();
        this.view.clearMessages();
        this._renderStartIndex = 0;
        this._renderEndIndex = 0;

        if (this._isEndlessScrollEnabled()) {
            this.view._scrollManager.resetEndlessState();
        }

        (this.options as IChatOptions).dataSource = dataSource;

        this._initDataSource();

        this._validateRemoteEndlessStartup();

        if ((this.options as IChatOptions).autoBind) {
            if (this._isRemoteEndlessScroll()) {
                this._requestLatestRemoteStartupRange();
            } else {
                this.dataSource.fetch();
            }
        }

        if (this.view) {
            this.view.refreshDataManager(this._dataManager);
        }
    }

    /**
     * Posts a new message to the chat.
     */
    postMessage(message: string | Partial<IMessage>): IMessage {
        if (this.currentMessageReplyId) {
            if (typeof message === "string") {
                message = { text: message };
            }
            message.replyToId = this.currentMessageReplyId;
        }

        return this._dataManager.postMessage(message, this.getUserId());
    }

    /**
     * Removes a message (marks as deleted).
     */
    removeMessage(message: IMessage): boolean {
        return this._dataManager.removeMessage(message);
    }

    /**
     * Updates an existing message.
     */
    updateMessage(message: IMessage, newData: Partial<IMessage>): IMessage | null {
        return this._dataManager.updateMessage(message, newData);
    }

    /**
     * Gets a message by its UID.
     */
    getMessageByUid(uid: string): IMessage | null {
        return this._dataManager.getMessageByUid(uid);
    }

    /**
     * Gets the data item for a message element.
     */
    dataItem(message: IKendoJQuery): IMessage | null {
        return this.view.dataItem(message);
    }

    /**
     * Gets the file data item for a file element.
     */
    fileDataItem(message: IMessage, file: IKendoJQuery): IFile | null {
        return this.view.fileDataItem(message, file);
    }

    /**
     * Clears all messages from the view.
     */
    clearMessages(): void {
        this.view.clearMessages();
    }

    /**
     * @internal
     * Renders a message in the view.
     */
    renderMessage(message: IMessage): void {
        this.view.renderMessage(message);
        this.accessibility.setupBubbleTabNavigation();
    }

    /**
     * @internal
     * Renders a pinned message.
     * The pinned message is positioned sticky inside the message list per Chat v3 spec.
     */
    renderPinnedMessage(message: IMessageReferenceData): void {
        const isOwnMessage = String(message.authorId) === String(this.getUserId());
        const options = this.options as IChatOptions;
        const pinnedMessageElement = $(options.messageReferenceTemplate({
            text: message.text,
            files: message.files,
            isDeleted: message.isDeleted,
            isOwnMessage,
            messages: options.messages,
            isPinMessage: true,
            renderCloseButton: true,
            renderFileMenuButton: false
        }));

        this.wrapper!.find(`[${REFERENCES.messageReferencePinWrapper}]`).remove();

        const messageList = this.wrapper!.find(DOT + STYLES.viewWrapper);
        if (messageList.length) {
            messageList.prepend(pinnedMessageElement);
        } else if (this._hasHeader) {
            pinnedMessageElement.insertAfter(this.wrapper!.find(DOT + STYLES.header));
        } else {
            this.wrapper!.prepend(pinnedMessageElement);
        }

        this.view._initFileMenus(pinnedMessageElement);
    }

    /**
     * Clears the pinned message.
     */
    clearPinnedMessage(): void {
        this._dataManager.clearPinnedMessage();
        this.wrapper!.find(DOT + STYLES.messagePinned).remove();
    }

    /**
     * Clears the reply state.
     */
    clearReplyState(): void {
        this.messageBox.removeReplyMessage();
        this.currentMessageReplyId = null;
    }

    /**
     * Scrolls the chat to the bottom.
     */
    scrollToBottom(): void {
        this.view.scrollToBottom();
    }

    /**
     * Scrolls to a specific message.
     */
    scrollToMessage(uid: string): boolean {
        return this.view.scrollToMessage(uid);
    }

    private _getEndlessDataCount(): number {
        return this._dataManager.getTotalCount();
    }

    private _getLatestRange(totalCount = this._getEndlessDataCount()): { startIndex: number; endIndex: number } {
        return this._endlessRangeManager.getLatestRange(this._getEndlessPageSize(), totalCount);
    }

    private _getTargetRange(targetIndex: number, totalCount = this._getEndlessDataCount()): { startIndex: number; endIndex: number } {
        return this._endlessRangeManager.getTargetRange(targetIndex, this._getEndlessPageSize(), totalCount);
    }

    private _isLatestRenderedRange(totalCount = this._getEndlessDataCount()): boolean {
        return this._endlessRangeManager.isLatestRenderedRange(this._renderEndIndex, totalCount);
    }

    private _hasRenderedLatestMessage(totalCount = this._getEndlessDataCount()): boolean {
        return this._endlessRangeManager.hasRenderedLatestMessage(this._renderEndIndex, totalCount);
    }

    private _isEndlessScrollEnabled(options: IChatOptions = this.options as IChatOptions): boolean {
        return options.scrollMode === "endless";
    }

    private _updateEndlessState(): void {
        const options = this.options as IChatOptions;

        if (!this._isEndlessScrollEnabled(options) || !this.view) {
            return;
        }

        const totalAvailable = this._getEndlessDataCount();

        this.view._scrollManager._allOlderLoaded = this._renderStartIndex <= 0;
        this.view._scrollManager._allNewerLoaded = this._renderEndIndex >= totalAvailable;
        this.view.refreshScrollState();
    }

    private _completeEndlessLoad(direction: RemoteLoadingState, callback?: () => void): void {
        requestAnimationFrame(() => {
            if (!this.view) {
                callback?.();
                return;
            }

            if (direction === "older" || direction === "all") {
                this.view._scrollManager._isLoadingOlder = false;
            }

            if (direction === "newer" || direction === "all") {
                this.view._scrollManager._isLoadingNewer = false;
            }

            this.view.refreshScrollState();
            callback?.();
        });
    }

    private _renderEndlessRange(
        startIndex: number,
        endIndex: number,
        options: { scrollToBottom?: boolean; scrollToMessageUid?: string } = {}
    ): void {
        const messages = this._dataManager.getMessagesInRange(startIndex, endIndex);

        this.view.clearMessages();

        messages.forEach((message: IMessage) => {
            this.renderMessage(message);
        });

        this._renderStartIndex = startIndex;
        this._renderEndIndex = endIndex;
        this._updateEndlessState();
        this.view.renderSuggestedActions();
        this.view.updateSeparator();
        this.accessibility.setupAriaAttributes();
        this.accessibility.setupBubbleTabNavigation();

        requestAnimationFrame(() => {
            if (!this.view) {
                return;
            }

            if (options.scrollToBottom) {
                this.scrollToBottom();
            } else if (options.scrollToMessageUid) {
                this.scrollToMessage(options.scrollToMessageUid);
            }

            this.view.refreshScrollState();
        });
    }

    private _prepareLatestMessages(): void {
        this._endlessScrollCoordinator.prepareLatestMessages();
    }

    private _navigateToReferencedMessage(id: string | number): void {
        const message = this._dataManager.getMessageById(id);
        const loadedMessage = this._dataManager.getLoadedMessageById(id);

        this.trigger(EVENTS.referencedMessageClick, { id });

        this._endlessScrollCoordinator.navigateToReferencedMessage(id, message, loadedMessage);
    }

    /**
     * Gets or sets the loading state (transforms send button to stop button).
     */
    loading(value?: boolean): boolean | void {
        if (value === undefined) {
            return this._loading;
        }

        this._loading = value;
        if (this.wrapper) {
            this.wrapper.toggleClass(STYLES.generating, value);
        }
        if (this.messageBox) {
            this.messageBox.loading(value);
        }
    }

    /**
     * @deprecated Use `loading()` instead. Will be removed in future version.
     */
    toggleSendButtonGenerating(value: boolean): void {
        this.loading(value);
    }

    /**
     * @internal
     * Refreshes the chat view when data changes.
     */
    refresh(e?: any): void {
        if (e?.action === "add") {
            this._bumpRemoteEndlessTotalForLocalAdd(e.items);
        }

        const state = this._createRefreshState(e);

        if (!e?.action) {
            this._refreshFull(state);
        }

        this._refreshPinnedState(state.pinnedMessage);

        if (e?.action === "sync" || e?.action === "add") {
            this._refreshSync(state);
        }

        this._finalizeRefresh();
    }

    private _bumpRemoteEndlessTotalForLocalAdd(items: IMessage[] | undefined): void {
        if (!this._isEndlessScrollEnabled() || !this._isRemoteEndlessScroll() || !items?.length) {
            return;
        }

        const loadedRange = this._dataManager.getLoadedRange();
        const newTotal = loadedRange.startIndex + this._dataManager.getLoadedCount();
        const currentTotal = this.dataSource.total();

        if (newTotal > currentTotal) {
            this.dataSource._total = newTotal;
            this.dataSource._pristineTotal = newTotal;
        }
    }

    private _createRefreshState(e?: any): IRefreshState {
        const data = this.dataSource.data ? this.dataSource.data() : this.dataSource.view();

        return {
            data,
            changedItems: e?.changedItems || [],
            pinnedMessage: this._dataManager.getPinnedMessage(),
            totalCount: this._getEndlessDataCount(),
            addedCount: e?.items?.length ?? 0
        };
    }

    private _refreshFull(state: IRefreshState): void {
        this._refreshNoDataTemplate(state.data);

        if (this._isEndlessScrollEnabled()) {
            this._refreshEndlessFull(state.totalCount);
            return;
        }

        this._refreshStandardFull(state.data);
    }

    private _refreshNoDataTemplate(data: IMessage[]): void {
        const options = this.options as IChatOptions;

        if (data.length === 0 && options.noDataTemplate) {
            this.view.showNoData(options.noDataTemplate());
        } else if (options.noDataTemplate) {
            this.view.hideNoData();
        }
    }

    private _refreshEndlessFull(totalCount: number): void {
        this.view._scrollManager._isLoadingOlder = true;
        this.view._scrollManager._isLoadingNewer = false;
        this.view.clearMessages();

        if (totalCount <= 0) {
            this._resetEmptyEndlessRefresh();
            return;
        }

        if (this._isRemoteEndlessScroll()) {
            this._refreshRemoteEndlessFull(totalCount);
            return;
        }

        this._refreshLocalEndlessFull(totalCount);
    }

    private _refreshRemoteEndlessFull(totalCount: number): void {
        const latestRange = this._getLatestRange(totalCount);

        if (!this._dataManager.isRangeLoaded(latestRange.startIndex, latestRange.endIndex)) {
            this._remoteRangeLoader.requestRange({
                startIndex: latestRange.startIndex,
                endIndex: latestRange.endIndex,
                direction: "latest",
                reason: "scroll-to-bottom",
                mode: "replace",
                loadingState: "all",
                scrollToBottom: true
            });
            return;
        }

        this._renderEndlessRange(latestRange.startIndex, latestRange.endIndex, { scrollToBottom: true });
        this._completeEndlessLoad("all");
    }

    private _refreshLocalEndlessFull(totalCount: number): void {
        const latestRange = this._getLatestRange(totalCount);

        this._renderEndlessRange(latestRange.startIndex, latestRange.endIndex, { scrollToBottom: true });
        this._completeEndlessLoad("older");
    }

    private _resetEmptyEndlessRefresh(): void {
        this._renderStartIndex = 0;
        this._renderEndIndex = 0;
        this._updateEndlessState();
        this.scrollToBottom();
        this._completeEndlessLoad("older");
    }

    private _refreshStandardFull(data: IMessage[]): void {
        this.view.clearMessages();
        data.forEach((item: IMessage) => {
            this.renderMessage(item);
        });
        this.scrollToBottom();
    }

    private _refreshPinnedState(pinnedMessage: IMessageReferenceData | null): void {
        if (pinnedMessage) {
            this.renderPinnedMessage(pinnedMessage);
            return;
        }

        this.wrapper!.find(`[${REFERENCES.messageReferencePinWrapper}]`).remove();
    }

    private _refreshSync(state: IRefreshState): void {
        const options = this.options as IChatOptions;

        if (options.noDataTemplate && !this.view.hasMessages()) {
            this.view.hideNoData();
        }

        const message = state.changedItems.length ? state.changedItems[0] : state.data[state.data.length - 1];
        const isOwnMessage = !!(message && String(message.authorId) === String(this.getUserId()));
        const wasNearBottom = this.view.isNearBottom();
        const isRenderedMessage = !!(message?.uid && this.view.hasMessage(message.uid));

        if (this._isEndlessScrollEnabled()) {
            this._refreshEndlessSync(message, isOwnMessage, wasNearBottom, isRenderedMessage, state.totalCount, state.addedCount);
            return;
        }

        this._refreshStandardSync(message, isOwnMessage, wasNearBottom);
    }

    private _refreshEndlessSync(
        message: IMessage,
        isOwnMessage: boolean,
        wasNearBottom: boolean,
        isRenderedMessage: boolean,
        totalCount: number,
        addedCount: number
    ): void {
        if (!this.view.hasMessages() && totalCount > 0) {
            this._prepareLatestMessages();
            return;
        }

        if (isOwnMessage && totalCount > 0) {
            this._refreshOwnEndlessSync(totalCount);
            return;
        }

        const extendsLatestRange = addedCount > 0 && this._canAppendOwnEndlessSync(totalCount);

        if (message && (isRenderedMessage || this._isLatestRenderedRange(totalCount) || extendsLatestRange)) {
            this._refreshVisibleEndlessSyncMessage(message, isOwnMessage, wasNearBottom, isRenderedMessage, totalCount);
            if (extendsLatestRange) {
                this.scrollToBottom();
            }
            return;
        }

        this._updateEndlessState();
    }

    private _refreshOwnEndlessSync(totalCount: number): void {
        if (this._isRemoteEndlessScroll() && this._canAppendOwnEndlessSync(totalCount)) {
            const newMessages = this._dataManager.getMessagesInRange(this._renderEndIndex, totalCount);

            newMessages.forEach((message: IMessage) => {
                this.renderMessage(message);
            });

            this._renderEndIndex = totalCount;
            this._updateEndlessState();
            this.scrollToBottom();
            return;
        }

        const latestRange = this._getLatestRange(totalCount);

        if (this._isRemoteEndlessScroll() && !this._dataManager.isRangeLoaded(latestRange.startIndex, latestRange.endIndex)) {
            this._prepareLatestMessages();
            return;
        }

        this._renderEndlessRange(latestRange.startIndex, latestRange.endIndex, { scrollToBottom: true });
    }

    private _canAppendOwnEndlessSync(totalCount: number): boolean {
        const loadedRange = this._dataManager.getLoadedRange();

        return this._renderEndIndex < totalCount
            && loadedRange.endIndex === totalCount
            && this._renderEndIndex >= loadedRange.startIndex;
    }

    private _refreshVisibleEndlessSyncMessage(
        message: IMessage,
        isOwnMessage: boolean,
        wasNearBottom: boolean,
        isRenderedMessage: boolean,
        totalCount: number
    ): void {
        this.renderMessage(message);

        if (!isRenderedMessage) {
            this._renderEndIndex = totalCount;
        }

        this._updateEndlessState();

        if (!isOwnMessage && wasNearBottom && message.uid) {
            this.view.scrollIncomingMessage(message.uid);
        }
    }

    private _refreshStandardSync(message: IMessage, isOwnMessage: boolean, wasNearBottom: boolean): void {
        if (!message) {
            return;
        }

        this.renderMessage(message);

        if (isOwnMessage) {
            this.scrollToBottom();
        } else if (wasNearBottom && message.uid) {
            this.view.scrollIncomingMessage(message.uid);
        }
    }

    private _finalizeRefresh(): void {
        this.view.renderSuggestedActions();
        this.view.updateSeparator();
        this.view.refreshScrollState();
        this.accessibility.setupAriaAttributes();
        this.accessibility.setupBubbleTabNavigation();
    }

    private _isRemoteEndlessScroll(): boolean {
        const options = this.options as IChatOptions;
        return !!(this._isEndlessScrollEnabled(options) && this.dataSource.options?.serverPaging);
    }

    private _prependLocalEndlessRange(startIndex: number, endIndex: number): void {
        const messages = this._dataManager.getMessagesInRange(startIndex, endIndex);

        if (!messages.length) {
            this._updateEndlessState();
            return;
        }

        this.view._scrollManager._isLoadingOlder = true;

        this.view._scrollManager.preserveScrollPosition(() => {
            this.view._messageRenderer.prependMessages(messages);
        });

        this._renderStartIndex = startIndex;
        this._updateEndlessState();
        this._completeEndlessLoad("older");
        this.accessibility.setupAriaAttributes();
        this.accessibility.setupBubbleTabNavigation();
    }

    private _appendLocalEndlessRange(startIndex: number, endIndex: number): void {
        const messages = this._dataManager.getMessagesInRange(startIndex, endIndex);

        if (!messages.length) {
            this._updateEndlessState();
            return;
        }

        this.view._scrollManager._isLoadingNewer = true;

        messages.forEach((message: IMessage) => {
            this.renderMessage(message);
        });

        this._renderEndIndex = endIndex;
        this._updateEndlessState();
        this._completeEndlessLoad("newer");
        this.accessibility.setupAriaAttributes();
        this.accessibility.setupBubbleTabNavigation();
    }

    _loadOlderMessages(): void {
        this._endlessScrollCoordinator.loadOlderMessages();
    }

    _loadNewerMessages(): void {
        this._endlessScrollCoordinator.loadNewerMessages();
    }

    /**
     * @internal
     */
    commandExecute(e: any): void {
        this._commandHandler.commandExecute(e);
    }

    /**
     * @internal
     */
    messageReply(message: IMessage): void {
        this._commandHandler.messageReply(message);
    }

    /**
     * @internal
     * Copies message text to clipboard.
     */
    messageCopy(message: IMessage): void {
        this._commandHandler.messageCopy(message);
    }

    /**
     * @internal
     * Pins a message.
     */
    messagePin(message: IMessage): void {
        this._commandHandler.messagePin(message);
    }

    /**
     * @internal
     * Deletes a message.
     */
    messageDelete(message: IMessage): void {
        this._commandHandler.messageDelete(message);
    }

    /**
     * @internal
     */
    messageContextMenuOpen(e: any): void {
        this._commandHandler.messageContextMenuOpen(e);
    }

    /**
     * Destroys the widget and cleans up resources.
     */
    destroy(): void {
        this._remoteRangeLoader.reset();

        if (this.dataSource) {
            this._unbindDataSource();
            this.dataSource = null;
        }

        if (this.view) {
            this.view.unbind();
            this.view.destroy();
            this.view = null;
        }

        if (this.messageBox) {
            this.messageBox.unbind();
            this.messageBox.destroy();
            this.messageBox = null;
        }

        if (this.messageContextMenu) {
            this.messageContextMenu.destroy();
        }

        if (this.wrapper) {
            this.wrapper.off();
            this.wrapper.empty();
            this.wrapper = null;
        }

        super.destroy();
    }
}
